import PlanBadge from '@/components/subscription/PlanBadge';
import { usePaywall } from '@/hooks/use-paywall';
import SettingsSection from '@/pages/settings/components/SettingsSection';
import { formatSettingsDate } from '@/pages/settings/components/format-settings-date';
import { Badge, Button, Stack, Text } from '@chakra-ui/react';
import {
  PLAN_IDS,
  PlanTier,
  type OrganizationBillingContext,
  type PlanIdValue,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { CurrentPlanFees } from './CurrentPlanFees';

const PLAN_ID_BY_TIER: Record<PlanTier, PlanIdValue> = {
  [PlanTier.FREE]: 'free-beta',
  [PlanTier.FREE_BETA]: 'free-beta',
  [PlanTier.ESSENTIAL]: PLAN_IDS.ESSENTIAL,
  [PlanTier.GROWTH]: PLAN_IDS.GROWTH,
};

export function OrganizationCurrentPlanSection({
  context,
}: {
  context: OrganizationBillingContext;
}) {
  const { t } = useTranslation();
  const { openPlans } = usePaywall();
  const subscription = context.subscription;
  const planKey = context.effectiveTier.toLowerCase();
  const ends = !!subscription?.cancelledAt || !!context.scheduledFreePlan;
  const isFree = context.effectiveTier === PlanTier.FREE;
  return (
    <SettingsSection
      title={t('settings.subscription.currentPlan.title')}
      description={t('settings.subscription.currentPlan.description')}
      actions={
        (context.channel === 'stripe' ||
          context.channel === 'none' ||
          (context.channel === 'shopify' &&
            isFree &&
            context.shopifyFreeApproval?.requirement === 'approved')) &&
        context.capabilities.canCommitPaidPlan &&
        context.cardSetup.exemption !== 'impersonation' ? (
          <Button
            variant="outline"
            onClick={() => {
              openPlans(context.effectiveTier === PlanTier.GROWTH ? 'growth' : 'essential');
            }}
          >
            {t('settings.subscription.currentPlan.manageButton')}
          </Button>
        ) : undefined
      }
    >
      <Stack align="flex-start" gap={2}>
        {subscription && !isFree && subscription.planId !== 'free-beta' ? (
          <PlanBadge planId={subscription.planId} size="sm" />
        ) : (
          <PlanBadge planId={PLAN_ID_BY_TIER[context.effectiveTier]} size="sm" showIcon={false} />
        )}
        <CurrentPlanFees
          planKey={planKey}
          currencyCode={context.billingCurrency ?? context.shopifyFreeApproval?.catalogCurrency}
        />
        {!isFree &&
          subscription?.currentPeriodEnd &&
          subscription.status === 'active' &&
          (ends || !subscription.isInGracePeriod) && (
            <Text fontSize="sm" color={ends ? 'orange.fg' : 'fg.muted'}>
              {t(
                ends
                  ? 'settings.subscription.billing.expiresOn'
                  : 'settings.subscription.billing.autoRenewOn',
                { date: formatSettingsDate(subscription.currentPeriodEnd, t('common.locale')) }
              )}
            </Text>
          )}
        {subscription?.status === 'pending' && (
          <Badge colorPalette="orange">{t('settings.subscription.status.pending')}</Badge>
        )}
        {subscription?.status === 'expired' && (
          <Badge colorPalette="red">{t('settings.subscription.billingManagement.expired')}</Badge>
        )}
        {context.scheduledFreePlan && (
          <Text fontSize="sm" color="fg.muted">
            {t('settings.subscription.billingManagement.nextPlan', {
              plan: t('pricing.plan.free.name'),
              date: formatSettingsDate(context.scheduledFreePlan.effectiveAt, t('common.locale')),
            })}
          </Text>
        )}
      </Stack>
    </SettingsSection>
  );
}
